import debounce from "lodash.debounce";
import fileDownload from "js-file-download";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Button } from "@mui/material";
import Stack from "@mui/material/Stack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import Title from "../../../common/Title";
import ClientChangeNumberList from "./ChangeNumberList";
import TerminateBatch from "../../../common/TerminateBatch";

import { copyData } from "../../../../utils/copy";
import useAxios from "../../../../hooks/useAxios";
import useAlert from "../../../../hooks/useAlert";
import { isUltraSim } from "../../../../utils/value";
import { CLIENTS } from "../../../../constants/value";
import {
  __getChangeNumber,
  __export,
  __checkStatus,
  __retryOperation
} from "../../../../api/sim";

const ClientChangeNumberBatch = () => {
  const { batch_id } = useParams();
  const [data, setData] = useState([]);
  const nav = useNavigate();

  const COPY_KEYS = [
    "id",
    "batch_id",
    "iccid",
    "status",
    "old_msisdn",
    "new_msisdn",
    "last_updated",
  ];

  const [metaData, setMetaData] = useState({
    page: 1,
    page_size: 20000,
    total: 1,
  });
  const [client, setClient] = useState();
  const [response, call, loading] = useAxios({ ...__getChangeNumber });
  const [recheckResponse, recheckCall, recheckLoading] = useAxios({
    ...__checkStatus(batch_id),
  });
  const { setAlertMessage } = useAlert();
  const [exportResponse, exportCall, exportLoading] = useAxios({ ...__export });
  const [retryResponse, retryCall, retryLoading] = useAxios({
    ...__retryOperation(batch_id),
  });

  const debouncedFetchData = debounce((query) => {
    call(query);
  }, 500);

  useEffect(() => {
    debouncedFetchData({
      params: {
        page_size: 20000,
        batch_id: batch_id,
      },
    });

    return () => {
      debouncedFetchData.cancel();
    };
  }, []);

  useEffect(() => {
    if (response && response.status) {
      setMetaData({
        page: response.data.page,
        page_size: response.data.page_size,
        total: response.data.total,
      });
      setData(response.data.data);

      if (response.data.data && response.data.data.length) {
        setClient(
          isUltraSim(response.data.data[0].iccid)
            ? CLIENTS.ultra
            : CLIENTS.telit
        );
      } else {
        nav("/client/change_number");
      }
    }
  }, [response]);

  useEffect(() => {
    if (recheckResponse && recheckResponse.status) {
      setAlertMessage({
        type: "success",
        message: "Change Number recheck queued",
      });
    }
  }, [recheckResponse]);

  useEffect(() => {
    if (exportResponse && exportResponse.status) {
      fileDownload(exportResponse.data, `change_number_export-${batch_id}.csv`);
    }
  }, [exportResponse]);

  useEffect(() => {
    if (retryResponse && retryResponse.status) {
      setAlertMessage({ type: "success", message: "Change Number retry queued" });
    }
  }, [retryResponse]);

  return (
    <Box sx={{ width: "100%" }}>
      <Title
        variant="h5"
        content={`Change Number Batch ID: ${batch_id}`}
        gutterBottom
      />
      {response && (
        <Grid container justifyContent="flex-start" spacing={2}>
          <Grid item sx={{ mb: "10px" }}>
            <Stack spacing={2} direction="row">
              <Button
                variant="contained"
                color="primary"
                disabled={loading || exportLoading}
                onClick={() =>
                  exportCall({
                    responseType: "blob",
                    params: {
                      action: "change_number",
                      batch_id: batch_id,
                    },
                  })
                }
              >
                <FileDownloadIcon sx={{ mr: "5px" }} /> Export
              </Button>
            </Stack>
          </Grid>
          <Grid item>
            <Stack spacing={2} direction="row">
              <Button
                variant="contained"
                color="secondary"
                disabled={loading || exportLoading}
                onClick={() => copyData(data, COPY_KEYS)}
              >
                <ContentCopyIcon sx={{ mr: "5px" }} /> Copy Data
              </Button>
            </Stack>
          </Grid>
          <Grid item sx={{ mb: "10px" }}>
            <Stack spacing={2} direction="row">
              <Button
                variant="contained"
                color="primary"
                disabled={loading || exportLoading}
                onClick={() =>
                  retryCall({
                    params: {
                      operation: "change_number",
                    },
                  })
                }
              >
                Retry
              </Button>
            </Stack>
          </Grid>
          {client === CLIENTS.telit && (
            <Grid item sx={{ mb: "10px" }}>
              <Stack spacing={2} direction="row">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading || exportLoading || recheckLoading}
                  onClick={() =>
                    recheckCall({
                      params: {
                        operation: "change_number",
                      },
                    })
                  }
                >
                  Recheck Status
                </Button>
              </Stack>
            </Grid>
          )}
          <Grid item sx={{ mb: "10px" }}>
            <TerminateBatch
              batch_id={batch_id}
              operation="change_number"
            />
          </Grid>
        </Grid>
      )}
      {response ? (
        <>
          <ClientChangeNumberList
            data={data}
            loading={loading}
            meta={{ ...metaData, page_size: 100 }}
            client={true}
            handleColumnClick={(key) => copyData(data, [key])}
          />
        </>
      ) : (
        <Title content="Please wait..." variant="subtitle2" gutterBottom />
      )}
    </Box>
  );
};

export default ClientChangeNumberBatch;
